import React, { useEffect, useState } from "react";
import api from "@/api/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader, KpiCard, Panel } from "@/components/shared/PageUi";
import CrudList from "@/components/shared/CrudList";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";
import { Truck, Users, MapPin, PackageCheck } from "lucide-react";

const DRIVER_STATUS = [
  { value: "active", label: "Aktiivinen" },
  { value: "off_duty", label: "Vapaalla" },
  { value: "inactive", label: "Ei käytössä" },
];
const VEH_STATUS = [
  { value: "available", label: "Vapaa" },
  { value: "in_transit", label: "Reissussa" },
  { value: "service", label: "Huollossa" },
  { value: "out_of_use", label: "Poissa" },
];
const VEH_TYPES = [
  { value: "truck", label: "Kuorma-auto" },
  { value: "van", label: "Pakettiauto" },
  { value: "trailer", label: "Perävaunu" },
];
const LOAD_STATUS = [
  { value: "planned", label: "Suunniteltu" },
  { value: "in_transit", label: "Matkalla" },
  { value: "delivered", label: "Toimitettu" },
  { value: "cancelled", label: "Peruutettu" },
];
const LICENSE_CLASSES = [
  { value: "B", label: "B" }, { value: "C", label: "C" }, { value: "CE", label: "CE" }, { value: "D", label: "D" }, { value: "T", label: "T" },
];

export default function LogisticsDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/logistics/stats").then((r) => setStats(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const drivers = {
    endpoint: "/logistics/drivers",
    testidPrefix: "log-drivers",
    emptyMessage: "Ei kuljettajia",
    defaults: { name: "", status: "active", total_deliveries: 0, rating: 0 },
    fields: [
      { key: "name", label: "Nimi", type: "text", required: true },
      { key: "phone", label: "Puhelin", type: "text" },
      { key: "email", label: "Sähköposti", type: "text" },
      { key: "license_number", label: "Ajokorttinumero", type: "text" },
      { key: "license_class", label: "Ajokorttiluokka", type: "select", options: LICENSE_CLASSES },
      { key: "license_expiry", label: "Ajokortin viim. voimassa", type: "date" },
      { key: "vehicle_plate", label: "Ajoneuvon rek.nro", type: "text" },
      { key: "current_location", label: "Nykyinen sijainti", type: "text" },
      { key: "status", label: "Tila", type: "select", options: DRIVER_STATUS },
      { key: "total_deliveries", label: "Toimituksia yht.", type: "number", step: "1" },
      { key: "rating", label: "Arvio (0-5)", type: "number", step: "0.1" },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "name", label: "Nimi" },
      { key: "phone", label: "Puhelin" },
      { key: "license_class", label: "Luokka" },
      { key: "vehicle_plate", label: "Ajoneuvo" },
      { key: "current_location", label: "Sijainti" },
      { key: "status", label: "Tila", format: "badge" },
    ],
  };

  const vehicles = {
    endpoint: "/logistics/vehicles",
    testidPrefix: "log-vehicles",
    emptyMessage: "Ei ajoneuvoja",
    defaults: { plate_number: "", type: "truck", status: "available", max_weight_kg: 0 },
    fields: [
      { key: "plate_number", label: "Rekisterinumero", type: "text", required: true },
      { key: "brand", label: "Merkki", type: "text" },
      { key: "model", label: "Malli", type: "text" },
      { key: "year", label: "Vuosi", type: "number", step: "1" },
      { key: "type", label: "Tyyppi", type: "select", options: VEH_TYPES },
      { key: "max_weight_kg", label: "Maks. kuorma (kg)", type: "number" },
      { key: "assigned_driver", label: "Kuljettaja", type: "text" },
      { key: "status", label: "Tila", type: "select", options: VEH_STATUS },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "plate_number", label: "Rek." },
      { key: "brand", label: "Merkki" },
      { key: "model", label: "Malli" },
      { key: "type", label: "Tyyppi", format: "badge" },
      { key: "max_weight_kg", label: "Kuorma kg", align: "right" },
      { key: "assigned_driver", label: "Kuljettaja" },
      { key: "status", label: "Tila", format: "badge" },
    ],
  };

  const routes = {
    endpoint: "/logistics/routes",
    testidPrefix: "log-routes",
    emptyMessage: "Ei reittejä",
    defaults: { name: "", estimated_km: 0, estimated_hours: 0 },
    fields: [
      { key: "name", label: "Reitin nimi", type: "text", required: true },
      { key: "start_location", label: "Alkupiste", type: "text" },
      { key: "end_location", label: "Loppupiste", type: "text" },
      { key: "estimated_km", label: "Arvio km", type: "number" },
      { key: "estimated_hours", label: "Arvio tunnit", type: "number", step: "0.1" },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "name", label: "Nimi" },
      { key: "start_location", label: "Lähtö" },
      { key: "end_location", label: "Pääte" },
      { key: "estimated_km", label: "Km", align: "right" },
      { key: "estimated_hours", label: "h", align: "right" },
    ],
  };

  const loads = {
    endpoint: "/logistics/loads",
    testidPrefix: "log-loads",
    emptyMessage: "Ei kuormia",
    defaults: { date: new Date().toISOString().slice(0, 10), status: "planned", items: [] },
    fields: [
      { key: "route_name", label: "Reitti", type: "text" },
      { key: "driver_name", label: "Kuljettaja", type: "text" },
      { key: "vehicle_plate", label: "Ajoneuvo (rek.nro)", type: "text" },
      { key: "date", label: "Päivämäärä", type: "date", required: true },
      { key: "status", label: "Tila", type: "select", options: LOAD_STATUS },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "load_number", label: "Nro" },
      { key: "date", label: "Pvm" },
      { key: "route_name", label: "Reitti" },
      { key: "driver_name", label: "Kuljettaja" },
      { key: "vehicle_plate", label: "Ajoneuvo" },
      { key: "status", label: "Tila", format: "badge" },
    ],
  };

  return (
    <div className="space-y-6" data-testid="logistics-dashboard">
      <PageHeader kicker="Sovellus" title="Logistiikka" description="Kuljettajat, ajoneuvot, reitit ja kuormat." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={<Users className="w-5 h-5" />} label="Aktiiviset kuljettajat" value={stats?.drivers_active ?? "—"} accent="text-emerald-300" loading={loading} testid="log-kpi-drivers" />
        <KpiCard icon={<Truck className="w-5 h-5" />} label="Vapaat ajoneuvot" value={stats?.vehicles_available ?? "—"} loading={loading} testid="log-kpi-vehicles" />
        <KpiCard icon={<MapPin className="w-5 h-5" />} label="Suunnitellut kuormat" value={stats?.loads_planned ?? 0} accent="text-blue-300" loading={loading} testid="log-kpi-planned" />
        <KpiCard icon={<PackageCheck className="w-5 h-5" />} label="Reissussa" value={stats?.loads_in_transit ?? 0} accent="text-amber-300" loading={loading} testid="log-kpi-transit" />
      </div>

      <Panel title="Hallinta">
        <Tabs defaultValue="loads" className="w-full">
          <TabsList className="bg-amber-500/10 mb-4">
            <TabsTrigger value="loads" data-testid="log-tab-loads">Kuormat</TabsTrigger>
            <TabsTrigger value="drivers" data-testid="log-tab-drivers">Kuljettajat</TabsTrigger>
            <TabsTrigger value="vehicles" data-testid="log-tab-vehicles">Ajoneuvot</TabsTrigger>
            <TabsTrigger value="routes" data-testid="log-tab-routes">Reitit</TabsTrigger>
            <TabsTrigger value="staff" data-testid="log-tab-staff"><Users className="w-3.5 h-3.5 mr-1.5" />Henkilökunta</TabsTrigger>
          </TabsList>
          <TabsContent value="loads"><CrudList config={loads} /></TabsContent>
          <TabsContent value="drivers"><CrudList config={drivers} /></TabsContent>
          <TabsContent value="vehicles"><CrudList config={vehicles} /></TabsContent>
          <TabsContent value="routes"><CrudList config={routes} /></TabsContent>
          <TabsContent value="staff"><ModuleStaffTab moduleKey="logistics" moduleLabel="Logistiikka" /></TabsContent>
        </Tabs>
      </Panel>
    </div>
  );
}
